import { lightTheme } from '@/customTheme'
import { ThemeProvider } from 'baseui'
import Icons from '../Editor/components/Icons'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectOpenModalTryPremium, selectUser } from '@/store/slices/user/selectors'
import { useHistory } from 'react-router'
import { useStyletron } from 'styletron-react'
import { useTranslation } from 'react-i18next'
import { SyncManager } from '@/firebase/SyncManager'
import useAppContext from '@/hooks/useAppContext'
import useModal from '@/hooks/useModal'
import { StatefulPopover } from 'baseui/popover'
import ButtonCustom from '@/components/ButtonCustom'
import { KIND, SHAPE } from 'baseui/button'
import { SizeButton } from '@/constants/sizeButton'
import { ShowModalType } from '../engine/common/constants'
import { showTextModal } from '@/components/ShowModal/textModalConfigs'
import Sidebar from './Sidebar'
import { DashboardPage, StickyHeader } from './components/DashboardPageLayout'
import SquareImageCard from '@/components/SquareImageCard/SquareImageCard'
// @ts-ignore
import EmptyProjectLottie from '@/assets/lottie/empty_project.lottie'
import { DotLottiePlayer } from '@dotlottie/react-player'
import { useLocation } from 'react-router-dom'
import { useAppDispatch } from '@/store/store'
import { TemplateConfig } from '@/interfaces/editor'
import { BtDiskFileUtils } from '@/shared/sync/dataTypes/BtDiskFileUtils'
import Loading from '@/components/Loading'
import defaultImage from '@assets/images/default-image.png'
import ScrollButton from './components/ScrollButton'
import HorizontalScroller from '@/components/HorizontalScroller'
import TitleWithSubAction from '@/components/TitleWithSubAction/TitleWithSubAction'
import NetworkStatusService from '@/shared/services/NetworkStatusService'
import { useTour } from '@reactour/tour'
import { setCallbackFromCreationPage } from '@/store/slices/editor/action'
import { selectCallbackFromCreationPage } from '@/store/slices/editor/selector'
import { useEditorContext } from '../engine'
import { customAmplitude } from '@/utils/customAmplitude'
import { selectDraftById, selectDrafts } from '@/store/slices/drafts/selectors'
// import { CloudManager } from '@/service-worker/sync/CloudManager'
import { DraftSyncVersion } from '@/shared/sync/dataTypes/DraftSyncVersion'
import { SyncStatus } from '@/shared/sync/dataTypes/SyncStatus'

const DURATION_TOAST_MESSAGE = 5000

function Projects() {
  const [projects, setProjects] = useState<any>([])
  const user = useSelector(selectUser)
  const [showToastMessage, setShowToastMessage] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const history = useHistory()
  const location = useLocation()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const openModalTryPremium = useSelector(selectOpenModalTryPremium)
  const { setIsLoadedJsonSuccess, resetEditorContext } = useAppContext()
  const { setShowModalType } = useModal()
  const [showBanner, setShowBanner] = useState(true)
  const [isFlushed, setIsFlushed] = useState(true);
  const { isOpen, setIsOpen} = useTour()
  const callbackFromCreationPage = useSelector(selectCallbackFromCreationPage)
  const editor = useEditorContext().editor
  const drafts = useSelector(selectDrafts);
  const projectContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    customAmplitude('Open', { Source: 'Projects' })
  }, [])

  useEffect(() => {
    if (callbackFromCreationPage) {
      dispatch(setCallbackFromCreationPage(null))

      if (editor) {
        editor.handlers.canvasHandler.canvas.discardActiveObject()
      }
      if(isOpen) {
        setIsOpen(false)
      }
    }
    setIsLoadedJsonSuccess(true);
  }, [])

  const isProjectReady = useCallback(async (project) => {
    if(!project) { return false }
    
    let syncItem = SyncManager.shared.getSyncItem(project.mostUpdatedVersion.draftId);
    let hasLocal = syncItem.hasLocal();
    if (!hasLocal || syncItem.isOutDated()) {
      return true
    }
    else {
      const hasDraft = !!drafts.find((draft) => draft.projectId === project.mostUpdatedVersion.draftId);
      return hasLocal && hasDraft && syncItem.mostUpdatedVersion.syncStatus !== SyncStatus.None;
    }
  }, [drafts]);

  const handleOpenDesign = async (id) => {
    let syncItem = SyncManager.shared.getSyncItem(id);
    if(!syncItem.mostUpdatedVersion.isSupportedOnWeb()) {
      showTextModal('PROJECT_NOT_SUPPORT')
    } else if (!syncItem.hasLocal() || syncItem.isOutDated()) {
      const online = await NetworkStatusService.getInstance().getLatestStatusConnection()
      if (!online) {
        setShowModalType(ShowModalType.NO_INTERNET)
        return
      }
      resetEditorContext()
      saveScrollPosition()
      history.push(`/design/${id}/edit`)
    } else if(syncItem.hasConflict()){
      showTextModal('PROJECT_CONFLICT')
    } else if (syncItem.hasLocal()){
      resetEditorContext()
      saveScrollPosition()
      history.push(`/design/${id}/edit`)
    }
  }

  const isValidProject = (id: string | null): boolean => {
    if (!id) { return false; }
    const syncItem = SyncManager.shared.getSyncItem(id);
    if (!syncItem.mostUpdatedVersion.isSupportedOnWeb()) {
      return false;
    }
    if (!syncItem.hasLocal() || syncItem.isOutDated()) {
      if (syncItem.isUploading()) {
        const TEN_MINUTES_MS = 10 * 60 * 1000;
        const modifiedDate = syncItem.mostUpdatedVersion?.modifiedDate?.getTime?.() || 0;
        return Date.now() - modifiedDate > TEN_MINUTES_MS;
      }
    }
    if (syncItem.hasConflict()) {
      return false;
    }
    return true;
  }

  const handleDuplicateProject = async (version: DraftSyncVersion) => {

  }

  const handleDeleteProject = async (project: TemplateConfig) => {
    SyncManager.shared.deleteProjects([project.draftGuid])
  }

  const renderProjets = async (isMounted: boolean) => {
    const syncItems = SyncManager.shared.getDraftSyncItems()
    let nonDeletedSyncItems = syncItems.filter (item => !(item.isDeleted() || item.shouldDeleteFromCloud()))
                                         .map(item => item);
    if (isMounted) {
      setProjects(nonDeletedSyncItems)
      if(SyncManager.shared.hasConnection) {
        setIsLoading(false)
      }
    }
  }

  const loadProjects = async (isMounted: boolean) => {
    if (isMounted) {
      setIsLoading(true)
    }

    if (SyncManager.shared.didCompleteSetup) {
      await renderProjets(isMounted)
    }
  }

  useEffect(() => {
    let isMounted = true;
    
    const handleUpdates = async () => {
      await renderProjets(isMounted)
    }
    const handleStateChange = async () => {
      await renderProjets(isMounted)
    }
    const handleProjectStateChange = () => {}

    SyncManager.shared.addEventListener('syncManagerHasUpdates', handleUpdates)
    SyncManager.shared.addEventListener('syncManagerHasStateChange', handleStateChange)
    SyncManager.shared.addEventListener('syncManagerHasProjectStateChange', handleProjectStateChange)

    return () => {
      isMounted = false;
      SyncManager.shared.removeEventListener('syncManagerHasUpdates', handleUpdates)
      SyncManager.shared.removeEventListener('syncManagerHasStateChange', handleStateChange)
      SyncManager.shared.removeEventListener('syncManagerHasProjectStateChange', handleProjectStateChange)
    };
  }, []);

  const saveScrollPosition = () => {
    if(!projectContainerRef.current) { return }
    const ratio = projectContainerRef.current.scrollTop / (projectContainerRef.current.scrollHeight - projectContainerRef.current.clientHeight)
    sessionStorage.setItem('scrollProject@', ratio.toString())
  }

  useEffect(() => {
    if(!projects || !projects.length || !projectContainerRef.current) { return }
    const scrollPosition = sessionStorage.getItem('scrollProject@')
    if(scrollPosition) {
      const ratio = parseFloat(scrollPosition)
      projectContainerRef.current.scrollTop = ratio * (projectContainerRef.current.scrollHeight - projectContainerRef.current.clientHeight)
      sessionStorage.removeItem('scrollProject@')
    }
  }, [projects])

  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout | null = null;

    (async () => {
      if (isMounted) {
        if(user) {
          loadProjects(isMounted)
        } else {
          setProjects([])
          setIsLoading(false)
        }
        if (openModalTryPremium && openModalTryPremium.source === 'save-project') {
          setShowToastMessage(true)
          timeoutId = setTimeout(() => {
            if (isMounted) {
              setShowToastMessage(false)
            }
          }, DURATION_TOAST_MESSAGE)
        }
      }
    })()

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [location, SyncManager.shared, user])

  useEffect(() => {
    const handleLeaveProject = function (e) {
      if (!isFlushed) {
        e.preventDefault()
        e.stopPropagation()
        this.alert('You have unsaved changes. Are you sure you want to leave?')
      }
    }
    window.addEventListener('beforeunload', handleLeaveProject)
    return () => {
      window.removeEventListener('beforeunload', handleLeaveProject)
    }
  }, [isFlushed])

  return (
    <ThemeProvider theme={lightTheme}>
      <>
        <Sidebar />
        {/* create page */}
        {location.pathname.includes('projects') ? (
          <DashboardPage
            ref={projectContainerRef}
            id="container-projects"
          >
            <StickyHeader gap="32px">
            {/* banner - hide for now */}
            {/* <div
              style={{
                position: 'relative',
                height: '64px',
                width: '100%',
                background: SyncManager.shared.currentUserId && user?.userIsPremium ? '#E6F7F3' : lightTheme.colors.grayScale50,
                borderRadius: '12px',
                zIndex: 2,
                display: showBanner ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 20px',
              }}
            >
              <p
                style={{
                  ...lightTheme.typography.Small16Bold,
                  fontWeight: 600,
                }}
              >
                {t('Access your projects anywhere.')} <span style={{
                  ...lightTheme.typography.Small16Bold,
                  fontWeight: 400,
                }}>{SyncManager.shared.currentUserId && user?.userIsPremium
                  ? t('Projects are automatically synced across all your devices.')
                  : t('Upgrade to Premium to sync across all your devices.')}</span>
              </p>
              <button
                className={css({
                  padding: '8px 16px',
                  height: '40px',
                  minWidth: SyncManager.shared.currentUserId && user?.userIsPremium ? '111px' : '176px',
                  width: 'fit-content',
                  ...lightTheme.typography.Small14Semibold,
                  background: SyncManager.shared.currentUserId && user?.userIsPremium ? lightTheme.colors.white : '#FCD34D',
                  border: 'none',
                  borderRadius: '20px',
                  margin: '0 40px 0 16px',
                  ':hover': {
                    background: SyncManager.shared.currentUserId && user?.userIsPremium ? lightTheme.colors.grayScale20 : '#F7CF4B',
                  },
                  ':active': {
                    background: SyncManager.shared.currentUserId && user?.userIsPremium ? lightTheme.colors.grayScale50 : '#F2CB4A',
                  },
                  cursor: 'pointer',
                })}
                onClick={e => {}}
              >
                {SyncManager.shared.currentUserId && user?.userIsPremium ? t('Learn more') : t('Upgrade to Premium')}
              </button>
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  top: '20px',
                  right: '20px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  position: 'absolute',
                }}
                onClick={() => setShowBanner(false)}
              >
                <Icons.Close size={24} fill={lightTheme.colors.grayScale600} />
              </div>
            </div> */}
            <div style={{ display: 'flex', flexDirection: 'row', paddingBottom: '32px' }} className="heading">
                <h2 style={{ ...lightTheme.typography.TitleBold }}>{t('Projects')}</h2>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '4px',
                    marginLeft: '16px',
                    height: '32px',
                    marginTop: '8px'
                  }}
                >
                  <span
                    style={{
                      background: lightTheme.colors.grayScale50,
                      ...lightTheme.typography.Small12medium,
                      fontWeight: 700,
                      width: 'fit-content',
                      minWidth: '48px',
                      height: '24px',
                      borderRadius: '4px',
                      padding: '0px 8px',
                    }}
                  >
                    {t('BETA')}
                  </span>
                  <ButtonCustom
                    onClick={() => {}}
                    type={SizeButton.SMALL}
                    kind={KIND.minimal}
                    shape={SHAPE.square}
                    tooltip={{
                      content: <div style={{...lightTheme.typography.Small12regular, color: lightTheme.colors.white, lineHeight: '16px', textAlign: 'center'}}><p>{t('Your web projects live here.')}</p><p style={{color: 'rgba(82, 181, 224, 1)', textDecoration: 'underline', cursor: 'pointer'}} onClick={() => {
                        window.open(
                          'https://support.bazaart.com/en/content/where-are-all-my-bazaart-projects?share=2kZQBmYC9VCIiTRzwqIB5vV70eOMjLSW',
                          '_blank'
                        )
                      }}>{t('Learn more')}</p></div>,
                      placement: 'bottom',
                    }}
                    style={{
                      borderRadius: '50%',
                    }}
                  >
                    <Icons.HelpIcon />
                  </ButtonCustom>
                </div>
                {projects.length ? (
                  <ButtonCustom
                    type={SizeButton.STANDARD}
                    kind="primary"
                    onClick={() => history.push('/create')}
                    style={{
                      minWidth: '139px',
                      width: 'max-content',
                      padding: '0px 18px 0px 12px',
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '6px',
                      justifyContent: 'center',
                      marginLeft: 'auto'
                    }}
                  >
                    <Icons.PlusIcon size={24} fill={'#fff'} />
                    <p
                      style={{
                        ...lightTheme.typography.Small14Semibold,
                        color: '#fff',
                        margin: 0,
                      }}
                    >
                      {t('New project')}
                    </p>
                  </ButtonCustom>
                ) : null}
              </div>
            </StickyHeader>
            {/*  */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
                position: 'relative',
                height: '100%',
                zIndex: 1,
              }}
            >
              {projects.length ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    paddingBottom: '32px',
                    minWidth: '586px',
                  }}
                  className="wrap-projects"
                >
                  {projects.map((project, index) => {
                    return (
                      <ProjectItem
                        key={`${project.mostUpdatedVersion.draftId}-${project.mostUpdatedVersion?.statusUpdateDate}`}
                        handleOpenDesign={handleOpenDesign}
                        isProjectReady={isProjectReady}
                        user={SyncManager.shared.currentUserId}
                        handleDuplicateProject={() => handleDuplicateProject(project) }
                        project={project}
                        isValidProject={isValidProject(project?.mostUpdatedVersion?.draftId)}
                      />
                    )
                  })}
                </div>
              ) : (
                !isLoading && (!projects.length) ? <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    gap: '24px',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <DotLottiePlayer
                    src={EmptyProjectLottie}
                    autoplay
                    loop
                    style={{
                      height: '106px',
                      width: '150px',
                    }}
                  ></DotLottiePlayer>
                  <h2 style={{ ...lightTheme.typography.Header20bold }}>{t('No projects yet')}</h2>
                  <ButtonCustom
                    type={SizeButton.STANDARD}
                    kind="primary"
                    onClick={() => history.push('/create')}
                    style={{
                      minWidth: '139px',
                      width: 'max-content',
                      padding: '0px 18px 0px 12px',
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '6px',
                      justifyContent: 'center',
                    }}
                  >
                    <Icons.PlusIcon size={24} fill={'#fff'} />
                    <p
                      style={{
                        ...lightTheme.typography.Small14Semibold,
                        color: '#fff',
                        margin: 0,
                      }}
                    >
                      {t('New project')}
                    </p>
                  </ButtonCustom>
                </div> : null
              )}
            </div>
            {/* loader */}
            {
              isLoading && SyncManager.shared.currentUserId ? <div
              style={{
                position: 'absolute',
                top: 'calc(50% + 32px)',
                transform: 'translateY(-50%)',
                left: 0,
                right: 0,
                zIndex: 1000,
                transition: 'all .4s ease-in',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Loading />
            </div> : null
            }
          </DashboardPage>
        ) : projects.length ? (
          <RecentProjects projects={projects} handleOpenDesign={handleOpenDesign} isProjectReady={isProjectReady} handleDuplicateProject={handleDuplicateProject} isValidProjectFunc={isValidProject} />
        ) : null}
        {/* projects page */}
              
      </>
      <div
        style={{
          position: 'fixed',
          height: '46px',
          width: 'fit-content',
          minWidth: '541px',
          background: lightTheme.colors.blackGray,
          borderRadius: '24px',
          zIndex: 1,
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: showToastMessage ? 'flex' : 'none',
          flexDirection: 'row',
          alignItems: 'center',
          padding: '8px 8px 8px 16px',
        }}
      >
        <Icons.SaveProjectIcon />
        <p
          style={{
            ...lightTheme.typography.Small14regular,
            color: lightTheme.colors.white,
            margin: '0 16px 0 12px',
          }}
        >
          {t('Projects are auto save, You can continue editing them at anytime')}
        </p>
        <div
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: lightTheme.colors.grayScale800,
            cursor: 'pointer',
          }}
          onClick={() => setShowToastMessage(false)}
        >
          <Icons.Close size={24} fill={'#fff'} />
        </div>
      </div>
    </ThemeProvider>
  )
}

export default React.memo(Projects)

function ProjectItem(props) {
  const [css] = useStyletron()
  const { setShowModalType } = useModal()
  const [displayDotsIcon, setDisplayDotsIcon] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [screenshotUrl, setScreenshotUrl] = useState(null)
  const [screenshotRetryAttempt, setScreenshotRetryAttempt] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [isProjectReady, setIsProjectReady] = useState(false)

  // const [hoverDeleteButton, setHoverDeleteButton] = useState(false)
  const { t } = useTranslation()
  const location = useLocation()
  const itemRef = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const isMountedRef = useRef(true)
  let preloader: HTMLImageElement | null = null;
  let preloaderCleanup: (() => void) | null = null;

  // Handle async isProjectReady check
  useEffect(() => {
    if (!props.project || !props.isProjectReady) {
      setIsProjectReady(false)
      return
    }

    // Call the async function and await the result
    let cancelled = false
    props.isProjectReady(props.project).then((ready) => {
      if (!cancelled && isMountedRef.current) {
        setIsProjectReady(ready)
      }
    }).catch(() => {
      if (!cancelled && isMountedRef.current) {
        setIsProjectReady(false)
      }
    })

    return () => {
      cancelled = true
    }
  }, [props.project, props.isProjectReady])

  // Handle async isProjectReady check
  useEffect(() => {
    if (!props.project || !props.isProjectReady) {
      setIsProjectReady(false)
      return
    }

    // Call the async function and await the result
    let cancelled = false
    props.isProjectReady(props.project).then((ready) => {
      if (!cancelled && isMountedRef.current) {
        setIsProjectReady(ready)
      }
    }).catch(() => {
      if (!cancelled && isMountedRef.current) {
        setIsProjectReady(false)
      }
    })

    return () => {
      cancelled = true
    }
  }, [props.project, props.isProjectReady])
  
  const projectIsLoaded = props.project && props.project.draftId && isProjectReady

  useEffect(() => {
    if (!isHovered) return
    setDisplayDotsIcon(projectIsLoaded)
  }, [projectIsLoaded, isHovered])

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // Intersection Observer to track visibility
  useEffect(() => {
    if (!itemRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (isMountedRef.current) {
          setIsVisible(entry.isIntersecting);
        }
      },
      {
        root: null, // viewport
        rootMargin: '200px', // Load 200px before entering viewport
        threshold: 0.01 // Trigger when 1% visible
      }
    );

    observer.observe(itemRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Screenshot loading effect with visibility and abort control
  useEffect(() => {
    if (screenshotRetryAttempt > 1) {
      return;
    }

    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    let url = props.project.local?.screenshotUrl
    if (url && isMountedRef.current) {
      setScreenshotUrl(url);
    } else if (isVisible && isMountedRef.current) {
      // Only fetch if visible
      abortControllerRef.current = new AbortController();
      
      (async () => {
        try {
          let screenshotUrl = await SyncManager.shared.getScreenshotUrl(props.project, props.project.mostUpdatedVersion)
          if (!isMountedRef.current) {
            return;
          }
          preloader = new Image();
          preloader.onload = () => {
            if (isMountedRef.current) {
              setScreenshotUrl(screenshotUrl);
            }
          };
          preloader.onerror = (err) => {
            if (isMountedRef.current) {
              console.warn('Failed to preload screenshot:', err);
            }
          };

          preloader.src = screenshotUrl;

          preloaderCleanup = () => {
            if (preloader) {
              preloader.onload = null;
              preloader.onerror = null;
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError' && isMountedRef.current) {
            console.warn('Failed to load screenshot:', error);
          }
        }
      })();
    }

    // Cleanup on unmount
    return () => {
      if (preloaderCleanup) {
        preloaderCleanup();
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [screenshotRetryAttempt, isVisible])

  const retryLoadScreenshot = async () => {
    if (props.project.mostUpdatedVersion.syncStatus !== SyncStatus.None){
      await SyncManager.shared.setCachedScreenshotUrl(undefined, props.project.mostUpdatedVersion)
      setScreenshotRetryAttempt(screenshotRetryAttempt + 1);
    } else {
      setScreenshotRetryAttempt(0);
    }
  }

  return (
  <SquareImageCard
    ref={itemRef}
    data-draft-id={props.project.draftId}
    onMouseEnter={() => {
      setIsHovered(true)
      setDisplayDotsIcon(projectIsLoaded)
    }}
    onMouseLeave={() => {
      setIsHovered(false)
      setDisplayDotsIcon(false)
    }}
    className={location.pathname.includes('projects') ? 'project-item' : ''}
    onClick={() => props.handleOpenDesign(props.project.mostUpdatedVersion.draftId)}
    style={{
      ...props.style,
      ':hover': {
        backgroundColor: projectIsLoaded ? lightTheme.colors.grayScale100 : 'auto',
      },
      cursor: projectIsLoaded ? 'pointer' : 'auto',
    }}
    src={projectIsLoaded && screenshotUrl ? screenshotUrl : null}
    alt="project thumb"
    background="frame"
    isLoading={!projectIsLoaded}
    onImageError={event => {
      // Optional: keep your existing retry logic
      // retryLoadScreenshot()

      // Fallback: replace broken src with defaultImage (no broken icon)
      const imgElement = event.target as HTMLImageElement
      if (imgElement) {
        imgElement.onerror = null // prevent potential infinite loop
        imgElement.src = defaultImage
      }
    }}
    fallback={
      <div style={{ width: '40px', height: '40px' }}>
        <img src={defaultImage} style={{ width: '100%', height: '100%' }} alt="default thumb" />
      </div>
    }
  >
    <div style={{ position: 'absolute', top: '8px', right: '8px' }}></div>
    <div
      style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        display: displayDotsIcon ? 'block' : 'none',
      }}
      onClick={e => e.stopPropagation()}
    >
      {displayDotsIcon ? (
        <StatefulPopover
          popoverMargin={4}
          placement="bottom"
          returnFocus={false}
          focusLock={false}
          content={({ close }) => (
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '16px',
                backgroundColor: '#fff',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)',
                padding: '16px',
              }}
            >
              {/* <ButtonCustom
                shape={SHAPE.square}
                kind={KIND.minimal}
                type={SizeButton.STANDARD}
                onClick={() => {
                  props.handleDuplicateProject()
                  close()
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  padding: '8px!important',
                }}
                startEnhancer={<Icons.Duplicate />}
              >
                <p style={{ ...lightTheme.typography.Small14regular }}>{t('Duplicate')}</p>
              </ButtonCustom> */}
              {props.isValidProject && props.project._mostUpdatedVersion.scenes < 2 && !props.project.isCurrentVersionIsUploading() ? (
                <>
                  <ButtonCustom
                    shape={SHAPE.square}
                    kind={KIND.minimal}
                    type={SizeButton.STANDARD}
                    onClick={e => {
                      SyncManager.shared.idDownloadProject =
                        props.project.mostUpdatedVersion.draftId
                      setShowModalType(ShowModalType.DOWNLOAD_PROJECT)
                      // close()
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      padding: '8px!important',
                    }}
                    startEnhancer={<Icons.DownloadIcon />}
                  >
                    <p style={{ ...lightTheme.typography.Small14regular }}>{t('Download')}</p>
                  </ButtonCustom>
                  <div
                    style={{
                      height: '1px',
                      width: '100%',
                      margin: '8px 0',
                      background: lightTheme.colors.grayScale100,
                    }}
                  ></div>
                </>
              ) : null}
              <div
              // onMouseEnter={() => setHoverDeleteButton(true)}
              // onMouseLeave={() => setHoverDeleteButton(false)}
              >
                <ButtonCustom
                  shape={SHAPE.square}
                  kind={KIND.minimal}
                  type={SizeButton.STANDARD}
                  onClick={e => {
                    SyncManager.shared.idDeleteProject = [
                      props.project.mostUpdatedVersion.draftId,
                    ]
                    setShowModalType(ShowModalType.DELETE_PROJECT)
                    e.stopPropagation()
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    padding: '8px!important',
                    ':hover': {
                      color: '#FF0505',
                    },
                  }}
                  startEnhancer={<Icons.Delete fill={'#FF0505'} />}
                >
                  <p
                    className={css({
                      ...lightTheme.typography.Small14regular,
                      color: '#FF0505',
                    })}
                  >
                    {t('Delete')}
                  </p>
                </ButtonCustom>
              </div>
            </div>
          )}
          overrides={{
            Body: {
              style: ({ $theme }) => ({
                zIndex: 1000,
                transitionDuration: 'none',
                width: '224px',
              }),
            },
            Inner: {
              style: ({ $theme }) => ({
                transitionDuration: 'none',
              }),
            },
          }}
          onClose={() => {}}
          stateReducer={(a, b, c) => {
            return b
          }}
          triggerType="click"
          accessibilityType={'tooltip'}
        >
          <div>
            <ButtonCustom
              type={SizeButton.STANDARD}
              kind={KIND.secondary}
              style={{
                width: '40px',
                padding: '0 8px',
                background: '#fff',
                ':hover': {
                  background: '#fff!important',
                },
              }}
              onClick={() => {}}
            >
              <Icons.Dots fill={'#000'} />
            </ButtonCustom>
          </div>
        </StatefulPopover>
      ) : null}
    </div>
    {props.project._mostUpdatedVersion.scenes > 1 ? (
      <div
        style={{
          position: 'absolute',
          right: '8px',
          bottom: '8px',
          background: 'rgba(17, 17, 17, 0.75)',
          minWidth: '52px',
          maxWidth: 'fit-content',
          height: '21px',
          borderRadius: '100px',
          ...lightTheme.typography.Small12medium,
          color: lightTheme.colors.white,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0 10px',
        }}
      >
        {t('%d of %d', { arg1: 1, arg2: props.project._mostUpdatedVersion.scenes })}
      </div>
    ) : null}
  </SquareImageCard>
)

}


function RecentProjects({projects, handleOpenDesign, handleDuplicateProject, isProjectReady, isValidProjectFunc}) {
  const history = useHistory()
  const [t] = useTranslation()

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        paddingBottom: '32px',
        cursor: !SyncManager.shared.hasConnection ? 'not-allowed' : 'auto',
        opacity: !SyncManager.shared.hasConnection ? lightTheme.opacity.disabled : lightTheme.opacity.active,
        pointerEvents: !SyncManager.shared.hasConnection ? 'none' : 'auto',
      }}
    >
      <TitleWithSubAction
        title="Recent projects"
        subActionText="See all"
        onSubActionClick={() => {
          history.push('/create/projects')
          customAmplitude('Click', { Source: 'Recent projects see all' })
        }}
        titleStyle={{ ...lightTheme.typography.Header20bold, fontWeight: 600 }}
        containerStyle={{ marginBottom: 0 }}
      />
      <HorizontalScroller gap={24} arrowVerticalPosition="center" arrowButtonSize="large" arrowIconVariant="line" contentAlign="start" arrowPlacement="edge" arrowVisibility="always" edgeClearance="full" showFadeMask={false} pageScroll edgeBounce>
        {projects?.map((project, index) =>
          index < 9 ? (
            <ProjectItem
              key={project.mostUpdatedVersion.draftId}
              style={{ minWidth: `166px`, width: `166px`, height: `166px` }}
              src={project.screenshotUrl}
              project={project}
              isProjectReady={isProjectReady}
              handleOpenDesign={(id) => {
                handleOpenDesign(id)
                customAmplitude('Create', { Source: 'Recent projects' })
              }}
              handleDuplicateProject={() => handleDuplicateProject(project)}
              isValidProject={isValidProjectFunc(project.mostUpdatedVersion.draftId)}
            ></ProjectItem>
          ) : null
        )}
      </HorizontalScroller>
    </div>
  )
}
